/**
 * AgendaDocumentMappingPanel — Phase 2 screenshare content.
 *
 * Allows the host to map agenda items to page ranges in an approved PDF attachment
 * for autonomous screenshare page switching.
 */

import { useCallback, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { Loader2, X } from 'lucide-react'
import { meetingsAPI, agendaAPI } from '../../services/api'
import { useToast } from '../common/Toast'

function unwrapData(res) {
  return res?.data ?? res
}

function claimedPagesByOther(drafts, excludeId) {
  const claimed = new Set()
  for (const [id, draft] of Object.entries(drafts)) {
    if (id === excludeId || !draft.enabled) continue
    const start = parseInt(String(draft.startPage), 10)
    const end = parseInt(String(draft.endPage), 10)
    if (Number.isNaN(start) || Number.isNaN(end)) continue
    for (let p = start; p <= end; p++) claimed.add(p)
  }
  return claimed
}

function nextSuggestedStartPage(drafts, excludeId) {
  let max = 0
  for (const [id, draft] of Object.entries(drafts)) {
    if (id === excludeId || !draft.enabled) continue
    const end = parseInt(String(draft.endPage), 10)
    if (!Number.isNaN(end) && end > max) max = end
  }
  return max > 0 ? max + 1 : 1
}

function PageGrid({ pageCount, currentValue, claimedPages, onSelect, onClose }) {
  return (
    <div className="mt-1.5 bg-white border border-gray-200 rounded-xl p-2 max-h-28 overflow-y-auto">
      <div className="flex flex-wrap gap-1">
        {Array.from({ length: pageCount }, (_, i) => i + 1).map((p) => {
          const isSelected = parseInt(String(currentValue), 10) === p
          const isClaimed = claimedPages.has(p)
          return (
            <button
              key={p}
              type="button"
              title={isClaimed && !isSelected ? 'Used by another item' : undefined}
              onClick={(e) => { e.stopPropagation(); onSelect(p); onClose() }}
              className={[
                'relative w-7 h-7 rounded-lg text-xs font-semibold transition-colors shrink-0',
                isSelected
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-50 text-gray-700 hover:bg-primary-50 hover:text-primary-700 border border-gray-200',
              ].join(' ')}
            >
              {p}
              {isClaimed && !isSelected && (
                <span className="absolute top-0.5 right-0.5 w-1.5 h-1.5 rounded-full bg-amber-400 pointer-events-none" />
              )}
            </button>
          )
        })}
      </div>
      {claimedPages.size > 0 && (
        <p className="mt-1.5 flex items-center gap-1 text-xs text-gray-400">
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0" />
          Used by another item
        </p>
      )}
    </div>
  )
}

export default function AgendaDocumentMappingPanel({ meetingId, attachmentId, filename, pageCount, onClose }) {
  const toast = useToast()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [agendaItems, setAgendaItems] = useState([])
  const [existingMappings, setExistingMappings] = useState([])
  const [drafts, setDrafts] = useState({})
  const [openPicker, setOpenPicker] = useState(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [agendaRes, mappingsRes] = await Promise.all([
        agendaAPI.get(meetingId),
        meetingsAPI.listDocumentMappings(meetingId),
      ])
      const agendaData = unwrapData(agendaRes)
      const mappingsData = unwrapData(mappingsRes)

      const items = agendaData?.agenda ?? agendaData?.items ?? []
      const mappings = mappingsData?.mappings ?? []

      setAgendaItems(Array.isArray(items) ? items : [])
      setExistingMappings(Array.isArray(mappings) ? mappings : [])

      const draftMap = {}
      for (const item of Array.isArray(items) ? items : []) {
        const id = item.agendaItemId ?? item.id
        const existing = (Array.isArray(mappings) ? mappings : []).find(
          (m) => m.agenda_item_id === id,
        )
        if (existing) {
          draftMap[id] = {
            startPage: existing.start_page,
            endPage: existing.end_page,
            autoAdvanceMode: existing.auto_advance_mode ?? 'even_time_split',
            enabled: true,
          }
        } else {
          draftMap[id] = { startPage: '', endPage: '', autoAdvanceMode: 'even_time_split', enabled: false }
        }
      }
      setDrafts(draftMap)
    } catch (e) {
      toast.error('Could not load agenda items', e?.message || 'Request failed')
    } finally {
      setLoading(false)
    }
  }, [meetingId])

  useEffect(() => {
    void load()
  }, [load])

  const toggleEnabled = (id) => {
    setOpenPicker(null)
    setDrafts((prev) => {
      const current = prev[id] ?? {}
      if (current.enabled) {
        return { ...prev, [id]: { ...current, enabled: false } }
      }
      const suggested = nextSuggestedStartPage(prev, id)
      return {
        ...prev,
        [id]: {
          ...current,
          enabled: true,
          startPage: current.startPage || suggested,
        },
      }
    })
  }

  const updateDraft = (agendaItemId, key, value) => {
    setDrafts((prev) => ({
      ...prev,
      [agendaItemId]: { ...(prev[agendaItemId] ?? {}), [key]: value },
    }))
  }

  const handleSave = async () => {
    setSaving(true)
    const errors = []

    for (const item of agendaItems) {
      const id = item.agendaItemId ?? item.id
      const draft = drafts[id]
      if (!draft?.enabled) continue

      const start = parseInt(String(draft.startPage), 10)
      const end = parseInt(String(draft.endPage), 10)

      if (Number.isNaN(start) || start < 1) {
        errors.push(`"${item.title}": start page required`)
        continue
      }
      if (Number.isNaN(end) || end < start) {
        errors.push(`"${item.title}": end page must be ≥ start`)
        continue
      }
      if (pageCount != null && end > pageCount) {
        errors.push(`"${item.title}": end page exceeds ${pageCount}`)
        continue
      }

      try {
        await meetingsAPI.upsertDocumentMapping(meetingId, {
          agenda_item_id: id,
          attachment_id: attachmentId,
          start_page: start,
          end_page: end,
          auto_advance_mode: draft.autoAdvanceMode ?? 'even_time_split',
        })
      } catch (e) {
        errors.push(`"${item.title}": ${e?.message || 'Save failed'}`)
      }
    }

    for (const existing of existingMappings) {
      const draft = drafts[existing.agenda_item_id]
      if (draft && !draft.enabled) {
        try {
          await meetingsAPI.deleteDocumentMapping(meetingId, existing.id)
        } catch {
          // non-critical
        }
      }
    }

    setSaving(false)

    const enabledCount = agendaItems.filter((item) => {
      const id = item.agendaItemId ?? item.id
      return drafts[id]?.enabled
    }).length

    if (errors.length > 0) {
      toast.error('Some mappings could not be saved', errors.join('; '))
    } else if (enabledCount === 0) {
      toast.success('Mappings cleared', 'No items mapped.')
      onClose?.()
    } else {
      toast.success('Mappings saved', 'Slides will advance automatically with each agenda item.')
      onClose?.()
    }
  }

  return createPortal(
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">

        {/* Header */}
        <div className="flex items-start justify-between p-6 border-b border-gray-100">
          <div className="min-w-0 flex-1 pr-3">
            <h3 className="text-lg font-semibold text-gray-900">Map Agenda Items to Document Pages</h3>
            <p className="text-sm text-gray-500 mt-0.5 truncate">
              {filename}{pageCount != null ? ` · ${pageCount} pages` : ''}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 p-1 -mt-0.5 text-gray-400 hover:text-gray-600 rounded-lg transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center gap-2 text-gray-500 text-sm py-8 justify-center">
              <Loader2 className="w-5 h-5 animate-spin" />
              Loading…
            </div>
          ) : agendaItems.length === 0 ? (
            <p className="text-sm text-gray-500 py-4">No agenda items found.</p>
          ) : (
            <div className="space-y-2">
              <p className="text-xs text-gray-400 mb-3">
                Click a row to map pages. Rithmo advances slides automatically when each item starts.
              </p>
              {agendaItems.map((item, index) => {
                const id = item.agendaItemId ?? item.id
                const draft = drafts[id] ?? { startPage: '', endPage: '', autoAdvanceMode: 'even_time_split', enabled: false }
                const claimed = claimedPagesByOther(drafts, id)

                const startNum = parseInt(String(draft.startPage), 10)
                const endNum = parseInt(String(draft.endPage), 10)
                const hasOverlap = draft.enabled && !Number.isNaN(startNum) && !Number.isNaN(endNum) &&
                  Array.from({ length: endNum - startNum + 1 }, (_, i) => startNum + i).some((p) => claimed.has(p))

                return (
                  <div
                    key={id}
                    className={`rounded-xl border transition-colors ${
                      draft.enabled ? 'border-primary-200 bg-primary-50/30' : 'border-gray-100 bg-white hover:border-gray-200'
                    }`}
                  >
                    {/* Clickable row header */}
                    <button
                      type="button"
                      onClick={() => toggleEnabled(id)}
                      className="w-full flex items-center gap-3 px-4 py-3 text-left"
                    >
                      <div className={`w-5 h-5 rounded-md shrink-0 border-2 flex items-center justify-center transition-colors ${
                        draft.enabled ? 'bg-primary-600 border-primary-600' : 'border-gray-300'
                      }`}>
                        {draft.enabled && (
                          <svg className="w-3 h-3 text-white" viewBox="0 0 12 12" fill="none">
                            <path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`font-medium text-sm ${draft.enabled ? 'text-gray-900' : 'text-gray-500'}`}>
                          {index + 1}. {item.title}
                        </p>
                        {(item.plannedDurationSec ?? item.durationSec) != null && (
                          <p className="text-xs text-gray-400">
                            {Math.round((item.plannedDurationSec ?? item.durationSec ?? 0) / 60)} min
                          </p>
                        )}
                      </div>
                      {draft.enabled && draft.startPage && draft.endPage && (
                        <div className="shrink-0 flex items-center gap-1.5">
                          <span className="text-xs text-primary-600 font-medium">
                            pp. {draft.startPage}–{draft.endPage}
                          </span>
                          {hasOverlap && (
                            <span
                              className="w-1.5 h-1.5 rounded-full bg-amber-400"
                              title="Page range overlaps with another item"
                            />
                          )}
                        </div>
                      )}
                    </button>

                    {/* Expanded controls */}
                    {draft.enabled && (
                      <div className="px-4 pb-4" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-start gap-4 flex-wrap">

                          {/* Start page */}
                          <div>
                            <p className="text-xs text-gray-400 mb-1">Start page</p>
                            {pageCount ? (
                              <>
                                <button
                                  type="button"
                                  onClick={() => setOpenPicker(openPicker === `${id}-start` ? null : `${id}-start`)}
                                  className={`min-w-[3rem] text-center rounded-xl border px-3 py-1.5 text-sm font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-primary-300 ${
                                    draft.startPage
                                      ? 'border-primary-300 bg-primary-50 text-primary-700 hover:border-primary-400'
                                      : 'border-gray-200 bg-gray-50 text-gray-400 hover:border-primary-300'
                                  }`}
                                >
                                  {draft.startPage || '—'}
                                </button>
                                {openPicker === `${id}-start` && (
                                  <PageGrid
                                    pageCount={pageCount}
                                    currentValue={draft.startPage}
                                    claimedPages={claimed}
                                    onSelect={(p) => updateDraft(id, 'startPage', p)}
                                    onClose={() => setOpenPicker(null)}
                                  />
                                )}
                              </>
                            ) : (
                              <input
                                type="number"
                                min={1}
                                value={draft.startPage}
                                onChange={(e) => updateDraft(id, 'startPage', e.target.value)}
                                className="w-16 rounded-xl border border-gray-200 bg-gray-50 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
                              />
                            )}
                          </div>

                          {/* End page */}
                          <div>
                            <p className="text-xs text-gray-400 mb-1">End page</p>
                            {pageCount ? (
                              <>
                                <button
                                  type="button"
                                  onClick={() => setOpenPicker(openPicker === `${id}-end` ? null : `${id}-end`)}
                                  className={`min-w-[3rem] text-center rounded-xl border px-3 py-1.5 text-sm font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-primary-300 ${
                                    draft.endPage
                                      ? 'border-primary-300 bg-primary-50 text-primary-700 hover:border-primary-400'
                                      : 'border-gray-200 bg-gray-50 text-gray-400 hover:border-primary-300'
                                  }`}
                                >
                                  {draft.endPage || '—'}
                                </button>
                                {openPicker === `${id}-end` && (
                                  <PageGrid
                                    pageCount={pageCount}
                                    currentValue={draft.endPage}
                                    claimedPages={claimed}
                                    onSelect={(p) => updateDraft(id, 'endPage', p)}
                                    onClose={() => setOpenPicker(null)}
                                  />
                                )}
                              </>
                            ) : (
                              <input
                                type="number"
                                min={1}
                                value={draft.endPage}
                                onChange={(e) => updateDraft(id, 'endPage', e.target.value)}
                                className="w-16 rounded-xl border border-gray-200 bg-gray-50 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
                              />
                            )}
                          </div>

                          {/* Advance mode */}
                          <div className="ml-auto self-end flex gap-1.5 pb-0.5">
                            <button
                              type="button"
                              onClick={() => updateDraft(id, 'autoAdvanceMode', 'even_time_split')}
                              className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${
                                (draft.autoAdvanceMode ?? 'even_time_split') === 'even_time_split'
                                  ? 'bg-primary-600 text-white'
                                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                              }`}
                            >
                              Auto
                            </button>
                            <button
                              type="button"
                              onClick={() => updateDraft(id, 'autoAdvanceMode', 'manual_only')}
                              className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${
                                draft.autoAdvanceMode === 'manual_only'
                                  ? 'bg-primary-600 text-white'
                                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                              }`}
                            >
                              Manual
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end p-6 border-t border-gray-100">
          <button
            type="button"
            onClick={handleSave}
            className="btn-primary text-sm inline-flex items-center gap-2 disabled:opacity-60"
            disabled={saving || loading || agendaItems.length === 0}
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            Save
          </button>
        </div>

      </div>
    </div>,
    document.body,
  )
}
